import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Truck, 
  Package, 
  User, 
  Calendar,
  Clock,
  MapPin
} from 'lucide-react';

const OrderManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  const orders = [
    {
      id: 'ORD001',
      customerName: 'MedSupply Corporation',
      customerCode: 'MEDSUP001',
      orderDate: '2024-01-15',
      deliveryDate: '2024-01-17',
      status: 'Picking',
      priority: 'High',
      totalItems: 12,
      totalValue: '$4,250.00',
      shippingAddress: '123 Medical Center Dr, City, ST 12345',
      items: [
        { sku: 'MED001', name: 'Surgical Masks N95', qty: 500 },
        { sku: 'MED002', name: 'Latex Gloves Large', qty: 200 }
      ]
    },
    {
      id: 'ORD002',
      customerName: 'Healthcare Plus Inc',
      customerCode: 'HCPLUS002',
      orderDate: '2024-01-15',
      deliveryDate: '2024-01-16',
      status: 'Shipping',
      priority: 'Medium',
      totalItems: 8,
      totalValue: '$2,890.00',
      shippingAddress: '456 Health Blvd, City, ST 67890',
      items: [
        { sku: 'MED003', name: 'Syringes 10ml', qty: 100 },
        { sku: 'MED004', name: 'Bandages Sterile', qty: 150 }
      ]
    },
    {
      id: 'ORD003',
      customerName: 'Medical Solutions Group',
      customerCode: 'MEDSOL003',
      orderDate: '2024-01-14',
      deliveryDate: '2024-01-18',
      status: 'Received',
      priority: 'Low',
      totalItems: 15,
      totalValue: '$6,750.00',
      shippingAddress: '789 Care Center Ave, City, ST 11111',
      items: [
        { sku: 'MED005', name: 'IV Bags Saline', qty: 50 },
        { sku: 'MED001', name: 'Surgical Masks', qty: 300 }
      ]
    },
    {
      id: 'ORD004',
      customerName: 'Pharma Direct LLC',
      customerCode: 'PHARM004',
      orderDate: '2024-01-15',
      deliveryDate: '2024-01-16',
      status: 'Processing',
      priority: 'High',
      totalItems: 6,
      totalValue: '$1,950.00',
      shippingAddress: '321 Pharma Park, City, ST 22222',
      items: [
        { sku: 'MED002', name: 'Latex Gloves', qty: 100 }
      ]
    },
    {
      id: 'ORD005',
      customerName: 'Care Products International',
      customerCode: 'CAREINT005',
      orderDate: '2024-01-14',
      deliveryDate: '2024-01-19',
      status: 'Quality Check',
      priority: 'Medium',
      totalItems: 9,
      totalValue: '$3,450.00',
      shippingAddress: '654 International Way, City, ST 33333',
      items: [
        { sku: 'MED003', name: 'Syringes', qty: 75 },
        { sku: 'MED004', name: 'Bandages', qty: 200 }
      ]
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Received': 'bg-blue-100 text-blue-800',
      'Processing': 'bg-yellow-100 text-yellow-800',
      'Picking': 'bg-orange-100 text-orange-800',
      'Quality Check': 'bg-purple-100 text-purple-800',
      'Shipping': 'bg-green-100 text-green-800',
      'Delivered': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'High': 'text-red-600 bg-red-50 border-red-200',
      'Medium': 'text-yellow-600 bg-yellow-50 border-yellow-200',
      'Low': 'text-green-600 bg-green-50 border-green-200'
    };
    return colors[priority] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === 'all' || 
                         order.status.toLowerCase() === selectedFilter.toLowerCase();
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Management</h1>
        <p className="text-gray-600">Track and manage customer orders</p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search by Order ID or Customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4">
            <select 
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Orders</option>
              <option value="received">Received</option>
              <option value="processing">Processing</option>
              <option value="picking">Picking</option>
              <option value="quality check">Quality Check</option>
              <option value="shipping">Shipping</option>
              <option value="delivered">Delivered</option>
            </select>

            <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
              <Plus className="h-4 w-4" />
              <span>New Order</span>
            </button>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredOrders.map((order) => (
          <div key={order.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{order.id}</h3>
                  <p className="text-sm text-gray-600">{order.customerCode}</p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(order.priority)}`}>
                  {order.priority} Priority
                </div>
              </div>
              
              <div className="flex items-center space-x-2 mb-3">
                <User className="h-4 w-4 text-gray-400" />
                <p className="text-sm font-medium text-gray-900">{order.customerName}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">{order.totalValue}</p>
                  <p className="text-xs text-gray-500">{order.totalItems} items</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2 text-sm">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Ordered:</span>
                  <span className="font-medium text-gray-900">{order.orderDate}</span>
                </div>
                
                <div className="flex items-center space-x-2 text-sm">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Delivery:</span>
                  <span className="font-medium text-gray-900">{order.deliveryDate}</span>
                </div>
                
                <div className="flex items-start space-x-2 text-sm">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <span className="text-gray-600">Ship to:</span>
                    <p className="font-medium text-gray-900 text-xs">{order.shippingAddress}</p>
                  </div>
                </div>
              </div>

              {/* Items Preview */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Items ({order.items.length})</h4>
                <div className="space-y-2">
                  {order.items.slice(0, 2).map((item, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{item.name}</span>
                      <span className="font-medium text-gray-900">Qty: {item.qty}</span>
                    </div>
                  ))}
                  {order.items.length > 2 && (
                    <p className="text-xs text-gray-500">+{order.items.length - 2} more items</p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                    <Eye className="h-4 w-4" />
                  </button>
                  <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
                <button className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700 font-medium">
                  <Truck className="h-4 w-4" />
                  <span>Track</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderManagement;